import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@/features/auth/hooks/use-user';
import { useDeleteDocument } from '@/features/documents/hooks/use-delete-document';
import { TrackingReportPopulated } from '@/features/projects/kpi-tracking-entry/api/get-tracking-reports';
import { TrackingReportDocumentsList } from '@/features/projects/kpi-tracking-entry/components/tracking-report-documents-list';
import { useCreateTrackingReportMutation } from '@/features/projects/kpi-tracking-entry/hooks/use-create-tracking-report-mutation';
import {
  useDocumentManager,
  DocumentState,
  DocumentManager,
} from '@/features/projects/kpi-tracking-entry/hooks/use-document-manager';
import { useUpdateTrackingReportMutation } from '@/features/projects/kpi-tracking-entry/hooks/use-update-tracking-report-mutation';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import { ProjectDetails } from '@/features/projects/shared/api/get-project-details';
import { usePartnerOfContractorProject } from '@/features/projects/shared/hooks/use-partner-of-contractor-project';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { LabelInput } from '@/shared/components/ui/label-input';
import { LabelTextarea } from '@/shared/components/ui/label-textarea';
import {
  DocumentData,
  DocumentUploadDialog,
} from '@/shared/components/ui/upload/document-upload-dialog';
import { useDocumentMutations } from '@/shared/components/ui/upload/file-input-form';
import { getKpiUnitTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { toast } from '@/shared/hooks/use-toast';
import { KpiType, PartnerAlias, ProjectKpi } from '@/shared/types/api';
import {
  formatDate,
  formatNumberWithDots,
  parseFormattedNumber,
} from '@/shared/utils/format';

type TrackingReportFormProps = {
  project: ProjectDetails;
  isOpen: boolean;
  onClose: () => void;
  editMode?: boolean;
  trackingReport?: TrackingReportPopulated;
  isContractor?: boolean;
  kpis: ProjectKpi[];
};

export const TrackingReportDialog = ({
  isOpen,
  onClose,
  project,
  editMode = false,
  trackingReport,
  isContractor = false,
  kpis,
}: TrackingReportFormProps) => {
  const { data: partnerAlias } = usePartnerOfContractorProject({
    projectId: project.id,
  });

  const { t } = useTranslation('project', {
    keyPrefix: 'TrackingReports.TrackingReportsDialog',
  });
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [editDocument, setEditDocument] = useState<DocumentState | undefined>(
    undefined,
  );

  const documentManager = useDocumentManager(
    editMode,
    trackingReport?.documents,
  );

  const handleClose = () => {
    setDocumentDialogOpen(false);
    documentManager.reset();
    setEditDocument(undefined);
    onClose();
  };

  const onEditDocument = (document: DocumentState) => {
    setEditDocument(document);
    setDocumentDialogOpen(true);
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DialogContent>
          <DialogHeader className="pb-5">
            <DialogTitle className="text-[32px] font-[600]">
              {editMode ? t('title_edit') : t('title_add')}
            </DialogTitle>
          </DialogHeader>
          <TrackingReportForm
            project={project}
            documentManager={documentManager}
            onAddDocument={() => setDocumentDialogOpen(true)}
            onClose={handleClose}
            editMode={editMode}
            trackingReport={trackingReport}
            onEditDocument={onEditDocument}
            isContractor={isContractor}
            kpis={kpis}
            partnerAlias={partnerAlias}
          />
        </DialogContent>
      </Dialog>
      <DocumentUploadDialog
        isOpen={documentDialogOpen}
        onClose={() => {
          setDocumentDialogOpen(false);
          setEditDocument(undefined);
        }}
        onCustomSubmit={(data) => {
          if (editDocument?.tempId) {
            documentManager.removeDocument(undefined, editDocument.tempId);
          }
          documentManager.addDocument(data);
        }}
        onCustomEdit={(data) => {
          documentManager.updateDocument(data);
        }}
        projectId={project.id}
        onSubmitSuccess={() => {}}
        publicOnly
        document={
          editDocument
            ? ({
                id: editDocument.id,
                tempId: editDocument.tempId,
                title: editDocument.title,
                project_id: editDocument.metadata.project_id,
                type: editDocument.metadata.type,
                visibility: editDocument.metadata.visibility,
                association: editDocument.metadata.association,
                file_path: editDocument.metadata.file_path,
                creator_id: editDocument.metadata.creator_id,
                org_id: editDocument.metadata.org_id,
                file: editDocument.file,
              } as DocumentData)
            : undefined
        }
        editMode={!!editDocument}
      />
    </>
  );
};

const TrackingReportForm = ({
  project,
  documentManager,
  onAddDocument,
  onClose,
  editMode = false,
  trackingReport,
  onEditDocument,
  isContractor = false,
  kpis,
  partnerAlias,
}: {
  project: ProjectDetails;
  documentManager: DocumentManager;
  onAddDocument: () => void;
  onClose: () => void;
  editMode?: boolean;
  trackingReport?: TrackingReportPopulated;
  onEditDocument: (document: DocumentState) => void;
  isContractor?: boolean;
  kpis: ProjectKpi[];
  partnerAlias: PartnerAlias | null | undefined;
}) => {
  const { t } = useTranslation('project', {
    keyPrefix: 'TrackingReports.TrackingReportsDialog',
  });
  const date = new Date();

  const [description, setDescription] = useState(
    trackingReport?.description ?? '',
  );
  const [kpiValues, setKpiValues] = useState<Record<number, string>>(
    trackingReport
      ? trackingReport.tracking_entries.reduce(
          (acc, entry) => {
            acc[entry.kpi_id ?? 0] = formatNumberWithDots(entry.value) ?? '';
            return acc;
          },
          {} as Record<number, string>,
        )
      : {},
  );

  const handleKpiValueChange = (kpiId: number, value: string) => {
    const formatted = formatNumberWithDots(parseFormattedNumber(value));
    setKpiValues((prev) => ({
      ...prev,
      [kpiId]: formatted,
    }));
  };

  const hasAtLeastOneKpiValue = Object.values(kpiValues).some(
    (value) => value.trim() !== '',
  );

  const { mutate: createTrackingReport } = useCreateTrackingReportMutation();
  const { mutate: updateTrackingReport } = useUpdateTrackingReportMutation({
    projectId: project.id,
  });
  const { uploadDocument, updateDocument } = useDocumentMutations();
  const { mutate: deleteDocument } = useDeleteDocument();
  const { data: user } = useUser();

  const handleSave = async () => {
    if (!user) {
      return;
    }

    try {
      const newDocuments = documentManager.getNewDocuments();
      const uploadPromises = newDocuments.map((doc) =>
        uploadDocument.mutateAsync(doc.createData!),
      );
      const uploadedDocs = await Promise.all(uploadPromises);

      const updatedDocuments = documentManager.getUpdatedDocuments();
      const updatePromises = updatedDocuments.map((doc) =>
        updateDocument.mutateAsync(doc.updateData!),
      );
      await Promise.all(updatePromises);

      const documentIds = [
        ...uploadedDocs.map((doc) => doc.id),
        ...updatedDocuments.map((doc) => doc.id!),
        ...documentManager.documents
          .filter((doc) => doc.status === 'existing')
          .map((doc) => doc.id!),
      ];

      const removedDocuments = documentManager.getRemovedDocuments();
      if (editMode && removedDocuments.length > 0) {
        const deletePromises = removedDocuments.map((doc) =>
          deleteDocument({
            documentId: doc.id,
            documentPath: doc.path,
          }),
        );
        await Promise.all(deletePromises);
      }

      if (editMode) {
        const parsedKpiValues = Object.entries(kpiValues).reduce(
          (acc, [kpiId, value]) => {
            acc[Number(kpiId)] = parseFormattedNumber(value).toString();
            return acc;
          },
          {} as Record<number, string>,
        );
        updateTrackingReport(
          {
            currReportId: trackingReport!.id,
            description,
            kpiValues: parsedKpiValues,
            documentIds,
          },
          {
            onSuccess: () => {
              onClose();
            },
            onError: (error) => {
              toast({
                title: t('errors.upload_error'),
                description: error.message,
              });
            },
          },
        );
      } else {
        const parsedKpiValues = Object.entries(kpiValues).reduce(
          (acc, [kpiId, value]) => {
            acc[Number(kpiId)] = parseFormattedNumber(value).toString();
            return acc;
          },
          {} as Record<number, string>,
        );
        createTrackingReport(
          {
            projectId: project.id,
            description,
            kpiValues: parsedKpiValues,
            documentIds,
          },
          {
            onSuccess: () => {
              onClose();
            },
            onError: (error) => {
              toast({
                title: t('errors.upload_error'),
                description: error.message,
              });
            },
          },
        );
      }
    } catch (error) {
      toast({
        title: t('errors.upload_error'),
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <LabelInput
        label={t('subproject_label')}
        placeholder={trackingReport?.project.name ?? project.name}
        disabled
      />

      <LabelInput
        label={t('partner_label')}
        placeholder={partnerAlias?.alias ?? ''}
        disabled
      />
      <LabelTextarea
        label={t('description_label')}
        placeholder={t('description_placeholder')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        disabled={!isContractor}
      />
      {kpis.map((kpi) => (
        <KpiInputGroup
          key={kpi.id}
          kpi={kpi}
          value={kpiValues[kpi.id] || ''}
          onChange={(value) => handleKpiValueChange(kpi.id, value)}
          disabled={!isContractor}
        />
      ))}
      <LabelInput
        label={t('date_label')}
        placeholder={formatDate({
          date,
          formatType: 'digits',
        })}
        disabled
      />
      <TrackingReportDocumentsList
        documents={documentManager.documents}
        onRemoveDocument={documentManager.removeDocument}
        onAddDocument={onAddDocument}
        editMode={editMode}
        onEditDocument={onEditDocument}
        isContractor={isContractor}
      />
      {isContractor && (
        <div className="mt-8 flex justify-center">
          <Button
            variant={!hasAtLeastOneKpiValue ? 'outline' : 'greenGradient'}
            className="w-[160px]"
            onClick={handleSave}
            disabled={!hasAtLeastOneKpiValue}
          >
            {t('save_entry')}
          </Button>
        </div>
      )}
    </div>
  );
};

const KpiInputGroup = ({
  kpi,
  value,
  onChange,
  disabled = false,
}: {
  kpi: ProjectKpi;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('project', {
    keyPrefix: 'TrackingReports.TrackingReportsDialog',
  });
  const { tKpi, tKpiTypes, tKpiCard } = useKpiTranslations();
  const temp = tKpiCard('subtitle', {
    type: tKpi(kpi.type as KpiType),
    unit: getKpiUnitTranslation(tKpiTypes, kpi.unit),
  });
  return (
    <div className="flex flex-row gap-[89px]">
      <LabelInput
        label={`${kpi.name} `}
        placeholder={temp}
        disabled
        className="text-black"
      />
      <LabelInput
        label={t('entry_label')}
        placeholder={t('entry_placeholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
};
